// Relatórios → Partilhas: com quem a equipa partilhou negócios (outras agências e colegas da equipa),
// com as comissões e a % partilhada. Usa as contas únicas (src/Contas.js): só negócios com a comissão
// garantida (a partir do CPCV assinado). Nomes das agências tal como estão escritos (nunca juntar).
import { useState } from "react";
import { contasNegocio, faseComissao, dataGarantida } from "./Contas";

const CINZA = "#9ca3af";
const NAVY = "#12244f";
const fmtEur = (v) => Number(v || 0).toLocaleString("pt-PT", { maximumFractionDigits: 0 }) + " €";
const pctDe = (parte, todo) => todo ? Math.round(parte / todo * 100) : 0;
const primeiro = (nome) => String(nome || "").split(" ")[0];

// consultorId: vista do consultor (só os seus negócios e a sua parte). Sem consultorId: líder (equipa toda).
export function resumoPartilhas(contasLista, { consultorId = null } = {}) {
  const agencias = {};
  const porConsultor = {};
  const equipa = [];
  const cons = (l) => porConsultor[l.consultorId || l.nome] || (porConsultor[l.consultorId || l.nome] = { nome: l.nome, externas: [], equipa: [] });
  contasLista.forEach(c => {
    const internas = c.linhas.filter(l => !l.externo);
    const externas = c.linhas.filter(l => l.externo);
    const minhas = consultorId ? internas.filter(l => l.consultorId === consultorId) : internas;
    if (!minhas.length) return;
    const parteEquipa = minhas.reduce((s, l) => s + Number(l.reporting || 0), 0);
    const ref = c.negocio.imovel_ref || "Sem ID";
    externas.forEach(e => {
      const nome = e.imobiliaria || "Agência externa";
      const a = agencias[nome] || (agencias[nome] = { agencia: nome, negocios: 0, total: 0, equipa: 0, externo: 0, porRegistar: 0 });
      a.negocios++; a.total += Number(c.total || 0); a.equipa += parteEquipa; a.externo += Number(e.reporting || 0);
      if (!Number(e.reporting || 0)) a.porRegistar++;
      minhas.forEach(l => cons(l).externas.push({ ref, agencia: nome, total: Number(c.total || 0), parte: Number(l.reporting || 0), externo: Number(e.reporting || 0) }));
    });
    const ids = [...new Set(internas.map(l => l.consultorId || l.nome))];
    if (ids.length === 2) {
      const [a, b] = internas;
      equipa.push({ ref, total: Number(c.total || 0), lados: [a, b].map(l => ({ nome: l.nome, consultorId: l.consultorId, reporting: Number(l.reporting || 0), pct: pctDe(Number(l.reporting || 0), Number(c.total || 0)) })) });
      minhas.forEach(l => { const outro = internas.find(o => (o.consultorId || o.nome) !== (l.consultorId || l.nome)); cons(l).equipa.push({ ref, colega: outro?.nome || "", parte: Number(l.reporting || 0), pct: pctDe(Number(l.reporting || 0), Number(c.total || 0)) }); });
    }
  });
  const listaAgencias = Object.values(agencias).map(a => ({ ...a, pct: pctDe(a.externo, a.total) })).sort((x, y) => y.total - x.total);
  const listaCons = Object.values(porConsultor).filter(c => c.externas.length || c.equipa.length).sort((x, y) => x.nome.localeCompare(y.nome));
  return { agencias: listaAgencias, porConsultor: listaCons, equipa };
}

const th = { padding: "9px 12px", fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: CINZA, textAlign: "left", whiteSpace: "nowrap" };
const td = { padding: "9px 12px", fontSize: 12.5, borderTop: "1px solid #f3f4f6", whiteSpace: "nowrap" };
const dir = { textAlign: "right", fontVariantNumeric: "tabular-nums" };
const caixa = { background: "#fff", border: "1px solid #ece9e3", borderRadius: 14, overflow: "auto", marginBottom: 18 };
const Titulo = ({ t, sub }) => <div style={{ padding: "14px 16px 6px" }}><div style={{ fontWeight: 700, fontSize: 14, color: NAVY }}>{t}</div>{sub && <div style={{ fontSize: 11.5, color: CINZA, marginTop: 2 }}>{sub}</div>}</div>;

export function RelatorioPartilhas({ negocios, ctx, isLider, userId }) {
  const anos = [...new Set((negocios || []).filter(n => faseComissao(n) === "comissao").map(n => String(dataGarantida(n) || "").slice(0, 4)).filter(Boolean))].sort().reverse();
  const [ano, setAno] = useState("");
  const lista = (negocios || []).filter(n => !ano || String(dataGarantida(n) || "").startsWith(ano)).map(n => contasNegocio(n, ctx)).filter(c => c.fase === "comissao");
  const r = resumoPartilhas(lista, isLider ? {} : { consultorId: userId });
  const rotuloParte = isLider ? "Parte da equipa" : "Parte do Consultor";
  const totais = r.agencias.reduce((s, a) => ({ negocios: s.negocios + a.negocios, total: s.total + a.total, equipa: s.equipa + a.equipa, externo: s.externo + a.externo }), { negocios: 0, total: 0, equipa: 0, externo: 0 });
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12.5, color: "#6b7280" }}>Negócios com comissão garantida (a partir do CPCV assinado)</span>
        <select value={ano} onChange={e => setAno(e.target.value)} style={{ marginLeft: "auto", padding: "6px 10px", borderRadius: 8, border: "1.5px solid #ece9e3", fontSize: 12.5 }}>
          <option value="">Todos os anos</option>
          {anos.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      <div style={caixa}>
        <Titulo t="🏢 Com que agências partilhámos" sub="A parte da outra agência aparece a cinzento e não conta para a equipa" />
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={th}>Agência parceira</th><th style={{ ...th, ...dir }}>Negócios</th><th style={{ ...th, ...dir }}>Comissão total</th><th style={{ ...th, ...dir }}>{rotuloParte}</th><th style={{ ...th, ...dir }}>Parte da agência</th><th style={{ ...th, ...dir }}>% partilhada</th></tr></thead>
          <tbody>
            {r.agencias.length === 0 && <tr><td style={{ ...td, color: CINZA }} colSpan={6}>Sem partilhas com outras agências.</td></tr>}
            {r.agencias.map(a => (
              <tr key={a.agencia}>
                <td style={{ ...td, fontWeight: 600 }}>{a.agencia}</td>
                <td style={{ ...td, ...dir }}>{a.negocios}</td>
                <td style={{ ...td, ...dir }}>{fmtEur(a.total)}</td>
                <td style={{ ...td, ...dir, fontWeight: 700, color: NAVY }}>{fmtEur(a.equipa)}</td>
                <td style={{ ...td, ...dir, color: CINZA }}>{fmtEur(a.externo)}{a.porRegistar > 0 && <span title="A parte da outra agência não foi registada no processo" style={{ color: "#b45309", marginLeft: 6 }}>⚠ por registar</span>}</td>
                <td style={{ ...td, ...dir, color: CINZA }}>{a.pct}%</td>
              </tr>
            ))}
            {r.agencias.length > 1 && (
              <tr style={{ background: "#fafaf8" }}>
                <td style={{ ...td, fontWeight: 700 }}>Total</td>
                <td style={{ ...td, ...dir, fontWeight: 700 }}>{totais.negocios}</td>
                <td style={{ ...td, ...dir, fontWeight: 700 }}>{fmtEur(totais.total)}</td>
                <td style={{ ...td, ...dir, fontWeight: 700, color: NAVY }}>{fmtEur(totais.equipa)}</td>
                <td style={{ ...td, ...dir, fontWeight: 700, color: CINZA }}>{fmtEur(totais.externo)}</td>
                <td style={{ ...td, ...dir, fontWeight: 700, color: CINZA }}>{pctDe(totais.externo, totais.total)}%</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={caixa}>
        <Titulo t={isLider ? "👤 Por consultor" : "👤 As minhas partilhas"} sub="Com quem partilhou: outras agências e colegas da equipa" />
        {r.porConsultor.length === 0 && <div style={{ ...td, color: CINZA }}>Sem partilhas.</div>}
        {r.porConsultor.map(c => (
          <div key={c.nome} style={{ padding: "10px 16px", borderTop: "1px solid #f3f4f6" }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{c.nome} <span style={{ fontWeight: 500, fontSize: 11.5, color: CINZA }}>· {c.externas.length} com outras agências · {c.equipa.length} em equipa</span></div>
            {c.externas.map((e, i) => (
              <div key={"e" + i} style={{ fontSize: 12, display: "flex", gap: 10, flexWrap: "wrap", padding: "2px 0" }}>
                <span style={{ minWidth: 110 }}>🏠 {e.ref}</span>
                <span style={{ color: CINZA, flex: "1 1 160px" }}>🏢 {e.agencia}</span>
                <span>Parte do Consultor <strong style={{ color: NAVY }}>{fmtEur(e.parte)}</strong> ({pctDe(e.parte, e.total)}%)</span>
                <span style={{ color: CINZA }}>Agência {fmtEur(e.externo)} ({pctDe(e.externo, e.total)}%)</span>
              </div>
            ))}
            {c.equipa.map((e, i) => (
              <div key={"q" + i} style={{ fontSize: 12, display: "flex", gap: 10, flexWrap: "wrap", padding: "2px 0" }}>
                <span style={{ minWidth: 110 }}>🏠 {e.ref}</span>
                <span style={{ flex: "1 1 160px" }}>🤝 com {e.colega}</span>
                <span>Parte do Consultor <strong style={{ color: NAVY }}>{fmtEur(e.parte)}</strong> ({e.pct}%)</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      <div style={caixa}>
        <Titulo t="🤝 Em equipa" sub="Negócios partilhados entre dois consultores da Equipa Sónia Cunha" />
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={th}>Negócio</th><th style={th}>Consultores</th><th style={{ ...th, ...dir }}>Divisão</th><th style={{ ...th, ...dir }}>Comissão total</th><th style={{ ...th, ...dir }}>Reporting de cada um</th></tr></thead>
          <tbody>
            {r.equipa.length === 0 && <tr><td style={{ ...td, color: CINZA }} colSpan={5}>Sem negócios partilhados na equipa.</td></tr>}
            {r.equipa.map((e, i) => (
              <tr key={i}>
                <td style={td}>🏠 {e.ref}</td>
                <td style={td}>{e.lados.map(l => primeiro(l.nome)).join(" ↔ ")}</td>
                <td style={{ ...td, ...dir }}>{e.lados.map(l => l.pct + "%").join(" / ")}</td>
                <td style={{ ...td, ...dir }}>{fmtEur(e.total)}</td>
                <td style={{ ...td, ...dir }}>{e.lados.map(l => (isLider || l.consultorId === userId) ? fmtEur(l.reporting) : "🔒").join(" / ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
